import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AgentProxyService } from '@/services/agentProxyService';

// Query keys
export const queryKeys = {
  all: ['queries'] as const,
  history: (datasourceId?: string, databaseName?: string) => [
    ...queryKeys.all, 
    'history', 
    datasourceId, 
    databaseName
  ] as const,
};

// Execute SQL query
export const useExecuteQueryMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      datasourceId, 
      databaseName, 
      query, 
      limit,
      timeout,
      schema,
      connectionType 
    }: { 
      datasourceId: string; 
      databaseName: string; 
      query: string; 
      limit?: number;
      timeout?: number;
      schema?: string;
      connectionType?: string;
    }) => {
      console.log('Sending query request:', {
        datasourceId,
        databaseName,
        query: query.substring(0, 100) + (query.length > 100 ? '...' : ''),
        limit,
        timeout,
        schema,
        connectionType
      });
      
      // For PostgreSQL, prepend SET search_path when schema is specified
      let finalQuery = query;
      if (connectionType === 'postgresql' && schema && schema !== 'public') {
        // Prepend SET search_path to the query for PostgreSQL
        finalQuery = `SET search_path TO ${schema}, public; ${query}`;
        console.log('Modified query for PostgreSQL schema:', {
          originalQuery: `${query.substring(0, 50)}...`,
          schema,
          finalQuery: `${finalQuery.substring(0, 100)}...`
        });
      }
      
      // Use AgentProxyService for query execution  
      return AgentProxyService.executeQuery(
        datasourceId,
        finalQuery,
        databaseName,
        true // suppressToast for query editor
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate query history when a query succeeds
      void queryClient.invalidateQueries({ 
        queryKey: queryKeys.history(variables.datasourceId, variables.databaseName) 
      });
    },
    onError: (error: unknown) => {
      console.error('Query execution failed:', error);
      // Error handling is done in the component
    },
  });
};

// Get query history - not yet implemented in agent proxy
export const useQueryHistoryQuery = (datasourceId?: string, databaseName?: string) => {
  return useQuery({
    queryKey: queryKeys.history(datasourceId, databaseName),
    queryFn: () => {
      // Query history not yet implemented in agent proxy
      throw new Error('Query history not yet implemented');
    },
    enabled: false, // Disabled until implemented
  });
};


// Explain query
export const useExplainQueryMutation = () => {
  return useMutation({
    mutationFn: async ({
      datasourceId,
      databaseName,
      query,
    }: {
      datasourceId: string;
      databaseName: string;
      query: string;
    }) => {
      return AgentProxyService.explainQueryPlan(
        datasourceId,
        query,
        databaseName,
        true // suppressToast for query editor
      );
    },
  });
};

// Execute batch queries - not yet implemented in agent proxy
export const useBatchQueryMutation = () => {
  return useMutation({
    mutationFn: ({
      datasourceId: _datasourceId,
      databaseName: _databaseName,
      queries: _queries,
      stopOnError: _stopOnError,
    }: {
      datasourceId: string;
      databaseName: string;
      queries: string[];
      stopOnError?: boolean;
    }) => {
      // Batch queries not yet implemented in agent proxy
      throw new Error('Batch query execution not yet implemented');
    },
  });
};
